import { ConnectorType } from "@axio/graphql/generated/graphql";
import { ExternalPickedItem } from "../../components/common/general/ContentPicker/ContentPicker.types";

/**
 * Grouped attachment input ready for the attachExternalContent mutation.
 * Items are grouped by connector type since the mutation processes one
 * connector at a time.
 */
export interface GroupedAttachmentInput {
  connectorType: ConnectorType;
  externalItemIds: string[];
  items: ExternalPickedItem[]; // Original items for reference (e.g., display names)
}

export interface ConversionResult {
  /** Grouped attachments ready for mutation calls. */
  grouped: GroupedAttachmentInput[];
  /** Items that were rejected (e.g., folders, oversized files). */
  rejected: {
    item: ExternalPickedItem;
    reason: string;
  }[];
}

export interface ExternalItemJobResult {
  externalItemId: string;
  jobId?: string | null;
  success: boolean;
  message?: string | null;
}

export interface ExternalItemJobPair {
  item: ExternalPickedItem;
  jobId: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * Maximum file size for external attachments (100MB).
 * Aligns with backend validation.
 */
const MAX_FILE_SIZE_BYTES = 100 * 1024 * 1024; // 100MB

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

function validateExternalItem(item: ExternalPickedItem): string | null {
  // Reject folders - they cannot be attached directly
  // Note: ExternalPickedItem doesn't have a `type` field, but we can infer from context.
  // For safety, we'll allow items through unless we have explicit folder indicators.
  // The backend will perform final validation.

  // Reject oversized files
  if (item.sizeBytes && item.sizeBytes > MAX_FILE_SIZE_BYTES) {
    const sizeMB = (item.sizeBytes / (1024 * 1024)).toFixed(2);
    return `File "${item.name}" is too large (${sizeMB}MB). Maximum size is 100MB.`;
  }

  return null; // Valid
}

// ---------------------------------------------------------------------------
// Conversion
// ---------------------------------------------------------------------------

/**
 * Convert external picked items to grouped attachment input for mutations.
 *
 * Validates items and groups them by connector type. Returns both valid
 * grouped attachments and rejected items with reasons.
 *
 * @param items - Array of external picked items from ContentPicker
 * @returns Object with `grouped` (valid attachments) and `rejected` (invalid items)
 *
 * @example
 * ```tsx
 * const { grouped, rejected } = convertExternalPickedItemsToAttachmentInput([
 *   { kind: 'external', connectorType: ConnectorType.GoogleDrive, externalId: 'file-1', ... },
 *   { kind: 'external', connectorType: ConnectorType.GoogleDrive, externalId: 'file-2', ... },
 *   { kind: 'external', connectorType: ConnectorType.OneDrive, externalId: 'doc-1', ... },
 * ]);
 *
 * // grouped[0]: { connectorType: GoogleDrive, externalItemIds: ['file-1', 'file-2'], items: [...] }
 * // grouped[1]: { connectorType: OneDrive, externalItemIds: ['doc-1'], items: [...] }
 *
 * // rejected: []
 * ```
 */

export function convertExternalPickedItemsToAttachmentInput(items: ExternalPickedItem[]): ConversionResult {
  const valid: ExternalPickedItem[] = [];
  const rejected: ConversionResult["rejected"] = [];

  // Validate each item
  items.forEach((item) => {
    const error = validateExternalItem(item);
    if (error) {
      rejected.push({ item, reason: error });
    } else {
      valid.push(item);
    }
  });

  // Group valid items by connector type
  const groupedMap = new Map<ConnectorType, ExternalPickedItem[]>();

  valid.forEach((item) => {
    const existing = groupedMap.get(item.connectorType) ?? [];
    existing.push(item);
    groupedMap.set(item.connectorType, existing);
  });

  // Convert map to array of GroupedAttachmentInput
  const grouped: GroupedAttachmentInput[] = Array.from(groupedMap.entries()).map(([connectorType, groupItems]) => ({
    connectorType,
    externalItemIds: groupItems.map((i) => i.externalId),
    items: groupItems,
  }));

  return { grouped, rejected };
}

export function pairExternalItemsWithQueuedJobs(
  items: ExternalPickedItem[],
  itemResults: ExternalItemJobResult[]
): ExternalItemJobPair[] {
  const resultsById = new Map(itemResults.map((result) => [result.externalItemId, result]));

  return items.flatMap((item) => {
    const result = resultsById.get(item.externalId);

    if (!result?.success || !result.jobId) {
      return [];
    }

    return [
      {
        item,
        jobId: result.jobId,
      },
    ];
  });
}
